// Converted plain JavaScript version of enhancedAvailabilityUtils
// Logic preserved from original TypeScript file, with type annotations removed.
// Updated to work with real backend data instead of mock data

// Get unit availability for a specific unit
export const getUnitAvailability = (
  unitId,
  startDate,
  endDate,
  excludeBookingId,
  bookings = [],
  units = []
) => {
  const unit = units.find(u => u._id === unitId || u.id === unitId);
  if (!unit) {
    throw new Error(`Unit with ID ${unitId} not found`);
  }

  const slots = [];
  const currentDate = new Date(startDate);
  const finalDate = new Date(endDate);

  // Ensure we don't go beyond 2 years from now
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);
  if (finalDate > maxDate) {
    finalDate.setTime(maxDate.getTime());
  }

  let hasSpecialTiming = false;
  let totalRevenueOpportunity = 0;

  while (currentDate <= finalDate) {
    const dateStr = currentDate.toISOString().split('T')[0];
    const slot = getDateAvailabilityForUnit(unitId, dateStr, excludeBookingId, bookings, unit);

    if (slot.availabilityType !== 'standard' && slot.availabilityType !== 'blocked') {
      hasSpecialTiming = true;
    }

    if (slot.revenueOpportunity) {
      totalRevenueOpportunity += slot.revenueOpportunity;
    }

    slots.push(slot);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  const isFullyAvailable = slots.every(slot => slot.isSelectable);

  return {
    unitId,
    unitNumber: unit.unitCode || unit.unitName || unit.number,
    unitType: unit.unitType || unit.type,
    availabilitySlots: slots,
    isFullyAvailable,
    hasSpecialTiming,
    revenueOpportunity: totalRevenueOpportunity > 0 ? totalRevenueOpportunity : undefined
  };
};

// Get availability for a specific date and unit
const getDateAvailabilityForUnit = (
  unitId,
  date,
  excludeBookingId,
  bookings = [],
  unit = null
) => {
  const targetDate = new Date(date);

  // Don't allow booking in the past
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (targetDate < today) {
    return {
      date,
      availableFrom: '00:00',
      availableUntil: '00:00',
      availabilityType: 'blocked',
      isSelectable: false,
      conflictingBooking: {
        id: 'past-date',
        guestName: 'Past Date',
        checkIn: date,
        checkOut: date,
        status: 'blocked'
      }
    };
  }

  const conflictingBooking = bookings.find(booking => {
    if (booking.unitId !== unitId && booking.unit?._id !== unitId) return false;
    if (excludeBookingId && booking.id === excludeBookingId) return false;
    if (booking.status === 'rejected' || booking.status === 'cancelled') return false;

    const checkIn = new Date(booking.checkIn);
    const checkOut = new Date(booking.checkOut);

    return targetDate >= checkIn && targetDate < checkOut;
  });

  if (conflictingBooking) {
    return {
      date,
      availableFrom: '00:00',
      availableUntil: '00:00',
      availabilityType: 'blocked',
      isSelectable: false,
      conflictingBooking: {
        id: conflictingBooking.id,
        guestName: conflictingBooking.guestName,
        checkIn: conflictingBooking.checkIn,
        checkOut: conflictingBooking.checkOut,
        status: conflictingBooking.status
      }
    };
  }

  // Check for early checkout opportunities (previous day)
  const previousDay = new Date(targetDate);
  previousDay.setDate(previousDay.getDate() - 1);
  const previousDayStr = previousDay.toISOString().split('T')[0];

  const earlyCheckoutBooking = bookings.find(booking => {
    if (booking.unitId !== unitId && booking.unit?._id !== unitId) return false;
    if (booking.status === 'rejected' || booking.status === 'cancelled') return false;

    const checkOut = new Date(booking.checkOut);
    return checkOut.toISOString().split('T')[0] === previousDayStr &&
      booking.actualCheckOut &&
      booking.checkoutType === 'early';
  });

  if (earlyCheckoutBooking && earlyCheckoutBooking.actualCheckOut) {
    const earlyTime = new Date(earlyCheckoutBooking.actualCheckOut).toTimeString().slice(0, 5);
    const revenueOpportunity = calculateSameDayRevenue(unitId, earlyTime, unit);

    return {
      date,
      availableFrom: earlyTime,
      availableUntil: '23:59',
      availabilityType: 'early-checkout',
      isSelectable: true,
      revenueOpportunity
    };
  }

  // Check for late checkout impact (same day)
  const lateCheckoutBooking = bookings.find(booking => {
    if (booking.unitId !== unitId && booking.unit?._id !== unitId) return false;
    if (booking.status === 'rejected' || booking.status === 'cancelled') return false;

    const checkOut = new Date(booking.checkOut);
    return checkOut.toISOString().split('T')[0] === date &&
      booking.actualCheckOut &&
      booking.checkoutType === 'late';
  });

  if (lateCheckoutBooking && lateCheckoutBooking.actualCheckOut) {
    const lateTime = new Date(lateCheckoutBooking.actualCheckOut).toTimeString().slice(0, 5);

    return {
      date,
      availableFrom: lateTime,
      availableUntil: '23:59',
      availabilityType: 'late-checkout',
      isSelectable: true
    };
  }

  // Standard availability
  return {
    date,
    availableFrom: '11:00', // Standard checkout time
    availableUntil: '23:59',
    availabilityType: 'standard',
    isSelectable: true
  };
};

// Get available units for a date range
export const getAvailableUnitsForDateRange = (
  units,
  checkIn,
  checkOut,
  excludeBookingId,
  bookings = []
) => {
  const dateAvailabilities = [];
  const currentDate = new Date(checkIn);
  const finalDate = new Date(checkOut);

  while (currentDate < finalDate) {
    const dateStr = currentDate.toISOString().split('T')[0];
    const dateAvailability = getDateAvailabilityForAllUnits(units, dateStr, excludeBookingId, bookings);
    dateAvailabilities.push(dateAvailability);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dateAvailabilities;
};

// Get availability for all units on a specific date
const getDateAvailabilityForAllUnits = (
  units,
  date,
  excludeBookingId,
  bookings = []
) => {
  const availableUnits = [];
  const blockedUnits = [];
  const partiallyAvailableUnits = [];

  units.forEach(unit => {
    const unitId = unit._id || unit.id;
    const slot = getDateAvailabilityForUnit(unitId, date, excludeBookingId, bookings, unit);

    if (slot.isSelectable) {
      if (slot.availabilityType === 'standard') {
        availableUnits.push(unitId);
      } else {
        partiallyAvailableUnits.push({
          unitId: unitId,
          unitNumber: unit.unitCode || unit.unitName || unit.number,
          availableFrom: slot.availableFrom,
          availableUntil: slot.availableUntil,
          availabilityType: slot.availabilityType,
          revenueOpportunity: slot.revenueOpportunity
        });
      }
    } else {
      blockedUnits.push(unitId);
    }
  });

  return {
    date,
    availableUnits,
    blockedUnits,
    partiallyAvailableUnits,
    totalAvailable: availableUnits.length + partiallyAvailableUnits.length
  };
};

// Check for booking conflicts
export const checkBookingConflicts = (
  unitId,
  checkIn,
  checkOut,
  excludeBookingId,
  bookings = []
) => {
  const conflicts = [];
  const checkInDate = new Date(checkIn);
  const checkOutDate = new Date(checkOut);

  const conflictingBookings = bookings.filter(booking => {
    if (booking.unitId !== unitId && booking.unit?._id !== unitId) return false;
    if (excludeBookingId && booking.id === excludeBookingId) return false;
    if (booking.status === 'rejected' || booking.status === 'cancelled') return false;

    const bookingCheckIn = new Date(booking.checkIn);
    const bookingCheckOut = new Date(booking.checkOut);

    return checkInDate < bookingCheckOut && checkOutDate > bookingCheckIn;
  });

  conflictingBookings.forEach(booking => {
    conflicts.push({
      type: 'overlap',
      conflictingBooking: {
        id: booking.id,
        guestName: booking.guestName,
        checkIn: booking.checkIn,
        checkOut: booking.checkOut,
        status: booking.status
      },
      suggestedAlternatives: generateAlternativeSuggestions(unitId, checkIn, checkOut, booking, bookings)
    });
  });

  return conflicts;
};

// Generate alternative suggestions when conflicts occur
const generateAlternativeSuggestions = (
  unitId,
  checkIn,
  checkOut,
  conflictingBooking,
  bookings = []
) => {
  const suggestions = [];
  const nights = Math.ceil((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24));

  // Suggest dates after the conflicting booking
  const afterConflictDate = new Date(conflictingBooking.checkOut);
  const newCheckOut = new Date(afterConflictDate);
  newCheckOut.setDate(newCheckOut.getDate() + nights);

  suggestions.push({
    type: 'date',
    checkIn: afterConflictDate.toISOString().split('T')[0],
    checkOut: newCheckOut.toISOString().split('T')[0],
    reason: 'Available after current booking ends'
  });

  // Suggest dates before the conflicting booking
  const beforeConflictDate = new Date(conflictingBooking.checkIn);
  const newCheckIn = new Date(beforeConflictDate);
  newCheckIn.setDate(newCheckIn.getDate() - nights);

  if (newCheckIn >= new Date()) {
    suggestions.push({
      type: 'date',
      checkIn: newCheckIn.toISOString().split('T')[0],
      checkOut: beforeConflictDate.toISOString().split('T')[0],
      reason: 'Available before current booking starts'
    });
  }

  return suggestions;
};

// Calculate same-day revenue opportunity
const calculateSameDayRevenue = (unitId, availableFromTime, unit) => {
  const availableHours = 24 - parseInt(availableFromTime.split(':')[0]);
  const hourlyRate = unit ? (unit.cost || 100) / 24 : 100; // Calculate hourly rate from daily cost
  return Math.floor(availableHours * hourlyRate * 0.7); // 70% of hourly rate for same-day
};

// Get revenue opportunities for a date range
export const getRevenueOpportunities = (
  units,
  startDate,
  endDate,
  bookings = []
) => {
  const opportunities = [];
  const currentDate = new Date(startDate);
  const finalDate = new Date(endDate);

  while (currentDate <= finalDate) {
    const dateStr = currentDate.toISOString().split('T')[0];

    units.forEach(unit => {
      const unitId = unit._id || unit.id;
      const slot = getDateAvailabilityForUnit(unitId, dateStr, null, bookings, unit);

      if (slot.revenueOpportunity && slot.revenueOpportunity > 0) {
        opportunities.push({
          unitId: unitId,
          unitNumber: unit.unitCode || unit.unitName || unit.number,
          date: dateStr,
          availableFrom: slot.availableFrom,
          potentialRevenue: slot.revenueOpportunity,
          type: slot.availabilityType,
          description: slot.availabilityType === 'early-checkout'
            ? `Early checkout - available from ${slot.availableFrom}`
            : `Late checkout - available from ${slot.availableFrom}`
        });
      }
    });

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return opportunities.sort((a, b) => b.potentialRevenue - a.potentialRevenue);
};

// Validate booking selection
export const validateBookingSelection = (
  unitId,
  checkIn,
  checkOut,
  excludeBookingId,
  bookings = [],
  units = []
) => {
  const conflicts = checkBookingConflicts(unitId, checkIn, checkOut, excludeBookingId, bookings);
  const availability = getUnitAvailability(unitId, checkIn, checkOut, excludeBookingId, bookings, units);

  return {
    isValid: conflicts.length === 0,
    conflicts,
    availability,
    canProceed: conflicts.length === 0 && availability.isFullyAvailable
  };
};

export default {
  getUnitAvailability,
  getAvailableUnitsForDateRange,
  checkBookingConflicts,
  getRevenueOpportunities,
  validateBookingSelection
};
